import { isUndefined } from 'lodash-es';
import { type KeyboardEvent } from 'react';

import { TimeseriesSelectionAreaTooltip } from './TimeseriesSelectionAreaTooltip.js';
import { TimeseriesSelectionDatapoints } from './TimeseriesSelectionDatapoints.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { reduceExploreSelect } from '../../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { DynamicSelectionArea } from '../../../../core/components/selection-area/DynamicSelectionArea.js';
import { useSynchronizedSelection } from '../../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import type { RelativeAbsolutePosition } from '../../../../core/types/pointer-events.js';
import { getPercentageFromPixel } from '../../../../core/utils/get-percentage-from-pixel.js';
import { getSelectionPixels } from '../../../../core/utils/get-selection-pixels.js';
import {
  useInteractionModeState,
  useSelectState,
  useSetState,
} from '../../../hooks/store/selectors.js';
import { useChartInteractions } from '../../../hooks/useChartInteractions.js';
import { useTimeseriesChartId } from '../../../hooks/useTimeseriesChartId.js';
import { useTimeseriesOverlayRef } from '../../../hooks/useTimeseriesOverlayRef.js';
import { useTimeseriesSelectionAreaRef } from '../../../hooks/useTimeseriesSelectionAreaRef.js';
import { useTimeseriesXFormatter } from '../../../hooks/useTimeseriesXFormatter.js';
import { useXScales } from '../../../hooks/useXScale.js';

export const TimeseriesSelectionArea = () => {
  const bottomFormatter = useTimeseriesXFormatter();
  const selectionAreaRef = useTimeseriesSelectionAreaRef();
  const { currentScale: xScale } = useXScales();
  const chartRef = useTimeseriesOverlayRef();
  const setState = useSetState();
  const selectState = useSelectState();
  /* eslint-disable-next-line @typescript-eslint/no-deprecated */
  const { zoomX } = useChartInteractions();
  const { width } = useGridPlotSize();

  const interactionMode = useInteractionModeState();

  const { updateSelectionState } = useSynchronizedSelection();
  const chartId = useTimeseriesChartId();

  const hasBottomMagnifiers = !isUndefined(bottomFormatter);

  if (isUndefined(selectState) || !chartRef.current) {
    return null;
  }

  const { start, end, selectionPercentage, finished, accessible, variant } =
    selectState;

  const handleWrapperKeyDown = (event: KeyboardEvent) => {
    const canZoom = zoomX?.enabled;
    if (event.key === 'Enter' && canZoom) {
      chartRef.current?.focus();
    }
  };

  const handleSelectionChange = ({
    selection,
    accessible,
  }: {
    selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
    accessible: boolean;
  }) => {
    setState(
      reduceExploreSelect({
        start: selection[0],
        end: selection[1],
        finished: true,
        accessible,
        variant,
        selectionPercentage: getPercentageFromPixel(selection, width),
      }),
    );
    if (interactionMode === 'explore') {
      updateSelectionState?.({
        selection,
        chartId,
        scale: xScale,
      });
    }
  };

  const selectionPosition: [
    RelativeAbsolutePosition,
    RelativeAbsolutePosition,
  ] = finished
    ? getSelectionPixels(selectionPercentage, width, [start, end])
    : [start, end];

  return (
    <>
      {variant === 'select' ? (
        <>
          <TimeseriesSelectionDatapoints />
          <TimeseriesSelectionAreaTooltip />
        </>
      ) : null}

      <DynamicSelectionArea
        chartRef={chartRef}
        hasMagnifiers={hasBottomMagnifiers}
        selection={selectionPosition}
        variant={variant}
        focusable={accessible}
        interactable={finished}
        bottomFormatter={(value) => bottomFormatter(xScale.invert(value))}
        onKeyDown={handleWrapperKeyDown}
        onSelectionChange={handleSelectionChange}
        selectionAreaRef={selectionAreaRef}
      />
    </>
  );
};
